import React, { useState, useEffect } from 'react';
import { ShoppingBag, Eye, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend';

// Helper function to fix local and relative image URLs correctly for live server
const getCorrectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80';

    // Agar URL mein already https:// ya http:// hai toh check karein
    if (url.startsWith('http://') || url.startsWith('https://')) {
        // Agar localhost ya 127.0.0.1 hai toh live domain se replace karein
        if (url.includes('127.0.0.1') || url.includes('localhost')) {
            return url.replace(/https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?/, 'https://rudrakshenterprises.liveindiatech.com');
        }
        return url;
    }

    // Agar relative path '/' se shuru hota hai
    if (url.startsWith('/')) {
        // Check karein ki path mein pehle se media ya rudraksh_backend to nahi hai
        if (url.startsWith('/media/')) {
            return `https://rudrakshenterprises.liveindiatech.com${url}`;
        }
        return `https://rudrakshenterprises.liveindiatech.com${url}`;
    }

    return `https://rudrakshenterprises.liveindiatech.com/media/${url}`;
};

// Helper function to remove duplicate products based on ID or Name
const getUniqueProducts = (products) => {
    if (!products || !Array.isArray(products)) return [];
    return Array.from(new Map(products.map(item => [item.id || item.name, item])).values());
};

export default function Bestsellers({ products: propProducts, addToCart, onViewDetails, searchQuery }) {
    const [fetchedProducts, setFetchedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const sliderId = "slider-container-bestsellers-all";

    // Admin panel se kisi bhi category mein add hue naye products ko automatically fetch karne ke liye
    useEffect(() => {
        if (!propProducts || propProducts.length === 0) {
            setLoading(true);

            Promise.all([
                fetch(`${API_BASE_URL}/api/bestsellers/`).then(res => res.json()).catch(() => []),
                fetch(`${API_BASE_URL}/api/pujasamagri/`).then(res => res.json()).catch(() => []),
                fetch(`${API_BASE_URL}/api/herbalproducts/`).then(res => res.json()).catch(() => []),
                fetch(`${API_BASE_URL}/api/jaivikproducts/`).then(res => res.json()).catch(() => []),
                fetch(`${API_BASE_URL}/api/ayurvedicproducts/`).then(res => res.json()).catch(() => []),
                fetch(`${API_BASE_URL}/api/organicproducts/`).then(res => res.json()).catch(() => [])
            ])
                .then(([bestsellersData, pujaData, herbalData, jaivikData, ayurvedicData, organicData]) => {
                    const extractList = (data) => Array.isArray(data) ? data : (data.results || data.products || []);

                    const combinedList = [
                        ...extractList(bestsellersData),
                        ...extractList(pujaData),
                        ...extractList(herbalData),
                        ...extractList(jaivikData),
                        ...extractList(ayurvedicData),
                        ...extractList(organicData)
                    ];

                    setFetchedProducts(combinedList);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Error fetching all categories products:", err);
                    setLoading(false);
                });
        }
    }, [propProducts]);

    const rawProducts = (propProducts && propProducts.length > 0) ? propProducts : fetchedProducts;
    const uniqueRawProducts = getUniqueProducts(rawProducts);

    // Search query filtering
    const displayProducts = searchQuery && searchQuery.trim() !== ''
        ? uniqueRawProducts.filter(p =>
            (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        : uniqueRawProducts;

    // Auto-scroll effect (halka-halka auto sliding)
    useEffect(() => {
        if (displayProducts.length === 0) return;

        const container = document.getElementById(sliderId);
        if (!container) return;

        const interval = setInterval(() => {
            if (container) {
                if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
                    container.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    container.scrollBy({ left: 280, behavior: 'smooth' });
                }
            }
        }, 3500);

        return () => clearInterval(interval);
    }, [displayProducts, sliderId]);

    const scrollLeft = () => {
        const container = document.getElementById(sliderId);
        if (container) {
            container.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        const container = document.getElementById(sliderId);
        if (container) {
            container.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };

    // Agar products available nahi hain toh loading state ya null return hoga
    if (displayProducts.length === 0) {
        if (loading) {
            return (
                <div className="w-full py-12 text-center text-sm text-gray-400">
                    Loading products from backend...
                </div>
            );
        }
        return null;
    }

    return (
        <section className="w-full px-4 sm:px-8 md:px-12 lg:px-20 py-16 bg-white overflow-hidden border-b border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-10 sm:mb-14">
                <div className="text-center sm:text-left mb-4 sm:mb-0">
                    <div className="inline-flex items-center gap-1.5 text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">
                        <Sparkles size={14} /> Most Popular
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                        Bestsellers & All Products
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                        Explore our top-rated and newly added wellness products.
                    </p>
                </div>

                {/* Left / Right Navigation Buttons */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={scrollLeft}
                        className="w-10 h-10 rounded-full bg-orange-50 hover:bg-orange-600 text-orange-600 hover:text-white flex items-center justify-center transition border border-orange-200 cursor-pointer shadow-sm"
                        aria-label="Scroll Left"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={scrollRight}
                        className="w-10 h-10 rounded-full bg-orange-50 hover:bg-orange-600 text-orange-600 hover:text-white flex items-center justify-center transition border border-orange-200 cursor-pointer shadow-sm"
                        aria-label="Scroll Right"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {/* Scrollable Container */}
            <div
                id={sliderId}
                className="flex gap-6 sm:gap-8 overflow-x-auto scrollbar-hide py-6 px-2 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {displayProducts.map((product, index) => {
                    const primaryImage = getCorrectImageUrl(product.image);
                    const galleryImages = product.images && product.images.length > 0
                        ? product.images.map(img => getCorrectImageUrl(img.image))
                        : [];

                    return (
                        <div
                            key={`${product.id || product.name}-${index}`}
                            className="relative w-64 sm:w-72 bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between pt-16 pb-6 px-6 mt-12 group flex-shrink-0 cursor-pointer"
                        >
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32 bg-white rounded-2xl border-2 border-orange-200 shadow-md overflow-hidden flex items-center justify-center p-1">
                                <img
                                    src={primaryImage}
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                                    alt={product.name}
                                    className="w-full h-full object-contain group-hover:scale-110 transition duration-500 rounded-xl"
                                />
                            </div>

                            <span className="absolute top-3 right-3 bg-orange-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow z-10">
                                {product.discount || 'Off 10%'}
                            </span>

                            <div className="text-center mt-6">
                                <h3 className="font-extrabold text-gray-900 text-sm sm:text-base line-clamp-1">{product.name}</h3>
                                <p className="text-xs text-gray-400 mt-1 line-clamp-1">{product.description || 'Verified Quality Product'}</p>

                                <div className="mt-4 flex items-center justify-center gap-3">
                                    <span className="text-xs text-gray-400 line-through">₹{product.original_price || product.originalPrice}</span>
                                    <span className="text-lg font-black text-orange-600">₹{product.price}</span>
                                </div>

                                {galleryImages.length > 0 && (
                                    <div className="flex items-center justify-center gap-1.5 mt-3">
                                        {galleryImages.map((imgUrl, imgIdx) => (
                                            <div key={imgIdx} className="w-8 h-8 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 p-0.5">
                                                <img src={imgUrl} alt="extra" className="w-full h-full object-cover rounded" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2 relative z-20">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onViewDetails) onViewDetails(product);
                                    }}
                                    className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                                >
                                    <Eye size={14} /> Details
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        addToCart(product);
                                    }}
                                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                                >
                                    <ShoppingBag size={14} /> Add
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}